import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import Home from './menu/Home';
import Box from './menu/Letter_logic/letter_box_route';
import LetterBoxMain from './menu/Letter_logic/letter_box_main';
import Letter from './menu/Letter_logic/letter_input';
import LoginMain from './menu/login/login_main';
import SignupLogic from './menu/login/signup_logic';
import UnloginStart from './menu/login/unlogin_start';
import Setting from './menu/setting';
import Profile from './menu/profile';
import FriendAdd from './menu/Social/friend_add';
import BetaLetter from './menu/beta/beta_letter';
import { PageTransition } from './components/PageTransition';
import './index.css';

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated';

const PUBLIC_PATHS = ['/start', '/login', '/signup'];
const API_BASE = process.env.REACT_APP_API_URL || '';

const clearStoredSession = () => {
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('access_token');
  sessionStorage.removeItem('isLoggedIn');
  sessionStorage.removeItem('access_token');
};

const isJwtExpired = (token: string) => {
  const payload = token.split('.')[1];
  if (!payload) return false;

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = JSON.parse(window.atob(padded));
    return typeof decoded.exp === 'number' && decoded.exp * 1000 <= Date.now();
  } catch {
    // JWT가 아닌 토큰은 서버의 /user/me 응답으로 유효성을 판단한다.
    return false;
  }
};

const getStoredToken = () => {
  const readToken = (storage: Storage) => {
    const hasLoginState = storage.getItem('isLoggedIn') === 'true';
    const token = storage.getItem('access_token');

    if (!hasLoginState || !token || isJwtExpired(token)) {
      storage.removeItem('isLoggedIn');
      storage.removeItem('access_token');
      return null;
    }

    return token;
  };

  return readToken(localStorage) || readToken(sessionStorage);
};

function App() {
  const path = window.location.pathname;
  const isPublicPath = PUBLIC_PATHS.includes(path);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(() =>
    getStoredToken() ? 'checking' : 'unauthenticated'
  );

  const validateSession = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setAuthStatus('unauthenticated');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/user/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 401 || response.status === 403) {
        clearStoredSession();
        setAuthStatus('unauthenticated');
        return;
      }

      // 서버 장애나 일시적인 API 오류는 토큰 만료로 간주하지 않는다.
      setAuthStatus('authenticated');
    } catch {
      // 오프라인 상태에서도 유효기간이 남은 로컬 토큰은 유지한다.
      setAuthStatus('authenticated');
    }
  }, []);

  useEffect(() => {
    let validating = false;
    const revalidate = () => {
      if (validating) return;
      validating = true;
      validateSession().finally(() => { validating = false; });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidate();
    };

    revalidate();
    window.addEventListener('pageshow', revalidate);
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', revalidate);
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [validateSession]);

  useEffect(() => {
    if (authStatus === 'checking') return;

    if (authStatus === 'authenticated' && isPublicPath) {
      window.location.replace('/');
    } else if (authStatus === 'unauthenticated' && !isPublicPath) {
      window.location.replace('/login');
    }
  }, [authStatus, isPublicPath]);

  if (authStatus === 'checking') return null;
  if (authStatus === 'authenticated' && isPublicPath) return null;
  if (authStatus === 'unauthenticated' && !isPublicPath) return null;

  const renderPage = () => {
    if (path === '/start')    return <UnloginStart />;
    if (path === '/archive')  return <Box />;
    if (path === '/letterbox') return <LetterBoxMain />;
    if (path === '/write')    return <Letter />;
    if (path === '/login')    return <LoginMain />;
    if (path === '/signup')   return <SignupLogic />;
    if (path === '/setting')  return <Setting />;
    if (path === '/profile')  return <Profile />;
    if (path === '/friend')   return <FriendAdd />;
    if (path === '/beta')     return <BetaLetter />;
    return <Home />;
  };

  /** 리스트 형태인 페이지 경로 */
  const LIST_PAGES = ['/archive', '/letterbox', '/friend', '/beta'];
  const variant = LIST_PAGES.includes(path) ? 'slideUp' : 'fade';

  return (
    <AnimatePresence mode="wait">
      <PageTransition key={path} variant={variant}>
        {renderPage()}
      </PageTransition>
    </AnimatePresence>
  );
}

export default App;
